import {Box, Typography} from '@mui/material';
import React, {useMemo, useState} from 'react';
import StandardIndexHeader from './standard-index-header/StandardIndexHeader';
import './StandardIndex.scss';
import {t} from 'i18next';
import ArrButton from '../../../common/arr-button/ArrButton';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import AnalyzerDetailsFlyout from '../create-custom-index/select-analyzers/analyzer-details-flyout/AnalyzerDetailsFlyout';
import {
  useCustomIndexDocumentCount,
  useCustomIndexFields,
  useGetAllCustomIndexAnalyzers,
  useGetAnalyzers,
} from '../../../../hooks/ingestions/useIngestions';
import SelectAnalyzersTable from '../create-custom-index/select-analyzers/select-analyzers-table/SelectAnalyzerTables';

type AnalyzerResponseItem = {
  AnalyzerId?: number;
  AnalyzerID?: number;
  AnalyzerName?: string;
  AnalyzerKey?: string;
  analyzerID?: number;
  analyzerId?: number;
  analyzerName?: string;
  analyzerKey?: string;
  id?: number;
  name?: string;
  key?: string;
  AvailableForCustomIndex?: boolean;
  availableForCustomIndex?: boolean;
};

type CustomIndexFieldItem = {
  fieldID: number;
  fieldName: string;
  analyzerID: number;
  analyzerName: string;
  isDefault: boolean;
};

const getAnalyzers = (response: unknown): AnalyzerResponseItem[] => {
  if (Array.isArray(response)) {
    return response as AnalyzerResponseItem[];
  }

  if (
    response &&
    typeof response === 'object' &&
    Array.isArray((response as {analyzers?: unknown}).analyzers)
  ) {
    return (response as {analyzers: AnalyzerResponseItem[]}).analyzers;
  }

  return [];
};

const getAnalyzerId = (analyzer: AnalyzerResponseItem) =>
  analyzer.AnalyzerId ??
  analyzer.AnalyzerID ??
  analyzer.analyzerID ??
  analyzer.analyzerId ??
  analyzer.id;

const getAnalyzerName = (analyzer: AnalyzerResponseItem) =>
  analyzer.AnalyzerName ??
  analyzer.analyzerName ??
  analyzer.name ??
  analyzer.AnalyzerKey ??
  analyzer.analyzerKey ??
  analyzer.key ??
  '';

const isAvailableForCustomIndex = (analyzer: AnalyzerResponseItem) =>
  analyzer.AvailableForCustomIndex ?? analyzer.availableForCustomIndex ?? false;

const StandardIndex = () => {
  const {data: analyzerResponse} = useGetAllCustomIndexAnalyzers();
  const {data: analyzerDetailsResponse} = useGetAnalyzers();
  const {data: customIndexFields} = useCustomIndexFields();
  const {data: docCountData} = useCustomIndexDocumentCount();
  const [isAnalyzerDetailsFlyoutOpen, setIsAnalyzerDetailsFlyoutOpen] =
    useState<boolean>(false);

  const sortedAnalyzerDetailsResponse = useMemo(() => {
    const analyzers = getAnalyzers(analyzerResponse);
    const analyzerDetails = getAnalyzers(analyzerDetailsResponse);
    const availableAnalyzerIds = new Set(
      analyzers
        .filter((analyzer) => {
          const analyzerId = getAnalyzerId(analyzer);
          const analyzerDetail = analyzerDetails.find(
            (detail) => getAnalyzerId(detail) === analyzerId
          );
          return analyzerDetail && isAvailableForCustomIndex(analyzerDetail);
        })
        .map(getAnalyzerId)
        .filter((analyzerId): analyzerId is number => analyzerId !== undefined)
    );
    const prioritizedAnalyzerName = 'consilio standard text';
    return analyzerDetails
      .filter((analyzer) => availableAnalyzerIds.has(getAnalyzerId(analyzer)))
      .sort((a: AnalyzerResponseItem, b: AnalyzerResponseItem) => {
        const nameA = getAnalyzerName(a).toLowerCase();
        const nameB = getAnalyzerName(b).toLowerCase();
        const isPrioritizedAnalyzerA = nameA === prioritizedAnalyzerName;
        const isPrioritizedAnalyzerB = nameB === prioritizedAnalyzerName;
        if (isPrioritizedAnalyzerA && !isPrioritizedAnalyzerB) {
          return -1;
        }
        if (!isPrioritizedAnalyzerA && isPrioritizedAnalyzerB) {
          return 1;
        }
        return nameA.localeCompare(nameB);
      });
  }, [analyzerDetailsResponse, analyzerResponse]);

  const customIndexFieldItems = useMemo<CustomIndexFieldItem[]>(() => {
    const response = customIndexFields as unknown;
    return Array.isArray(response) ? (response as CustomIndexFieldItem[]) : [];
  }, [customIndexFields]);

  const selectedMetadataFieldNames = useMemo(
    () =>
      customIndexFieldItems.map((field) => {
        const matchedAnalyzer = Array.isArray(analyzerResponse)
          ? (analyzerResponse as AnalyzerResponseItem[]).find(
              (analyzer) =>
                Number(
                  analyzer.analyzerID ??
                    analyzer.analyzerId ??
                    analyzer.AnalyzerId ??
                    analyzer.id
                ) === Number(field.analyzerID) ||
                (analyzer.analyzerName ??
                  analyzer.AnalyzerName ??
                  analyzer.name) === field.analyzerName
            )
          : undefined;

        return {
          fieldId: field.fieldID,
          fieldName: field.fieldName,
          analyzerID: field.analyzerID,
          analyzerName: field.analyzerName,
          analyzerKey:
            matchedAnalyzer?.analyzerKey ??
            matchedAnalyzer?.AnalyzerKey ??
            matchedAnalyzer?.key ??
            '',
          isDefault: field.isDefault,
        };
      }),
    [analyzerResponse, customIndexFieldItems]
  );

  const selectedAnalyzerResponse = useMemo(() => {
    if (!Array.isArray(analyzerResponse)) {
      return [];
    }
    const selectedAnalyzerNames = new Set(
      customIndexFieldItems.map((field) => field.analyzerName)
    );
    return (analyzerResponse as AnalyzerResponseItem[]).filter((analyzer) =>
      selectedAnalyzerNames.has(
        analyzer.analyzerName ?? analyzer.AnalyzerName ?? analyzer.name ?? ''
      )
    );
  }, [analyzerResponse, customIndexFieldItems]);

  return (
    <Box data-testid="standard-index-root">
      <StandardIndexHeader />
      <Box
        className="standard-index-content"
        data-testid="standard-index-content"
      >
        <Box
          className="document-count-box"
          data-testid="standard-index-document-count-section"
        >
          <Box
            className="document-count-box-title"
            data-testid="standard-index-document-count-title-container"
          >
            <Typography
              className="document-count-title"
              data-testid="standard-index-document-count-title"
            >
              {t('indexManagement.standardIndex.documentCount')}
            </Typography>
          </Box>
          <Box
            className="total-count-box"
            data-testid="standard-index-total-count-container"
          >
            <Box
              className="total-count-header"
              data-testid="standard-index-total-count-header"
            >
              <Typography
                className="total-count-text"
                data-testid="standard-index-total-count-label"
              >
                {t('indexManagement.standardIndex.totalCount')}
              </Typography>
              <Typography
                className="doc-count"
                data-testid="standard-index-total-document-count"
              >
                {docCountData?.totalCount.toLocaleString()}{' '}
                {t('indexManagement.standardIndex.docs')}
              </Typography>
            </Box>
            <Box
              className="sec-grp-name-box"
              data-testid="standard-index-security-groups-container"
            >
              {docCountData?.securityGroupCounts.map((securityGroup) => (
                <Box
                  key={securityGroup.securityGroupId}
                  className="sec-grp"
                  data-testid={`standard-index-security-group-${securityGroup.securityGroupId}`}
                >
                  <Typography
                    className="sec-grp-name"
                    data-testid={`standard-index-security-group-${securityGroup.securityGroupId}-name`}
                  >
                    {securityGroup.securityGroupName}
                  </Typography>
                  <Typography
                    className="sec-grp-count"
                    data-testid={`standard-index-security-group-${securityGroup.securityGroupId}-count`}
                  >
                    {securityGroup.documentCount.toLocaleString()}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
        <Box
          className="metadata-fields"
          data-testid="standard-index-metadata-fields-section"
        >
          <Box
            className="metadata-fields-left-box"
            data-testid="standard-index-metadata-fields-content"
          >
            <Box
              className="fields-heading"
              data-testid="standard-index-metadata-fields-heading-container"
            >
              <Typography
                className="fields-heading-text"
                data-testid="standard-index-metadata-fields-heading"
              >
                {t('indexManagement.standardIndex.metadataFields')}
              </Typography>
            </Box>
            <Typography
              className="metadata-description"
              data-testid="standard-index-metadata-fields-description"
            >
              {t('indexManagement.standardIndex.metadataDescription')}
            </Typography>
          </Box>
          <ArrButton
            size="medium"
            dataTestId="analyzer-details-flyout-btn"
            aria-label="Open Analyzer Details"
            onClick={() => setIsAnalyzerDetailsFlyoutOpen(true)}
            endIcon={<KeyboardArrowRightIcon fontSize="medium" />}
          >
            {t('indexManagement.customIndex.readOnly.viewAnalyzers')}
          </ArrButton>
        </Box>
        <AnalyzerDetailsFlyout
          isOpen={isAnalyzerDetailsFlyoutOpen}
          analyzerResponse={sortedAnalyzerDetailsResponse}
          onClose={() => setIsAnalyzerDetailsFlyoutOpen(false)}
        />
        <SelectAnalyzersTable
          analyzerResponse={selectedAnalyzerResponse}
          selectedMetadataFieldNames={selectedMetadataFieldNames}
          analyzerColumnName={t('indexManagement.standardIndex.fields')}
          fullWidth={true}
        />
      </Box>
    </Box>
  );
};

export default StandardIndex;
